import { useEffect, useCallback, useState } from "react"
import {
  useLocation,
  useNavigate,
  matchPath,
  useNavigationType,
  NavigateFunction,
  Location,
  NavigationType,
} from "react-router-dom"
import { last } from "lodash"

const NO_REPEAT_PATHS = ["/maps/amenities/*"]

export const useNavigationHistory = () => {
  const location: Location = useLocation()
  const navigationType: NavigationType = useNavigationType()
  //NOTE: add pathname and searchParam to navigationStack to prevent some error when user clicks back
  const currentPath: string = `${location.pathname + location.search}`
  const [navigationStack, setNavigationStack] = useState<string[]>([
    currentPath,
  ])
  const navigate: NavigateFunction = useNavigate()

  // Sync stack with router on URL change.
  // Do NOT add `navigationStack` to deps or mutate `prevStack` — that caused
  // an infinite loop (pop-on-empty returned a new ref → re-fired the effect).
  useEffect(() => {
    setNavigationStack((prevStack) => {
      const latestPath = last(prevStack)
      // Already in sync — return same ref so React bails out.
      if (currentPath === latestPath) return prevStack
      switch (navigationType) {
        case "POP":
          // Empty stack: keep same ref to avoid re-firing effect.
          return prevStack.length > 0 ? prevStack.slice(0, -1) : prevStack
        case "REPLACE":
          return [...prevStack.slice(0, -1), currentPath]
        case "PUSH":
          return [...prevStack, currentPath]
        default:
          return prevStack
      }
    })
  }, [currentPath, navigationType])

  const goBack = useCallback((): void => {
    // Check if the navigation stack contains more than one entry and if the current path matches the repeat path pattern.
    // Prevent the page from navigating back repeatedly to previously visited place.
    const matchedNoRepeatPath = NO_REPEAT_PATHS.find((pattern) =>
      matchPath(pattern, location.pathname)
    )
    if (navigationStack.length > 1 && matchedNoRepeatPath) {
      const reversedStack = navigationStack.slice().reverse()
      const removedConsecutiveStack = reversedStack
        .filter((value, index) => {
          const isFirstItem = index === 0
          if (isFirstItem) return false
          const prevItem = reversedStack[index - 1]
          return !(
            matchPath(matchedNoRepeatPath, value) &&
            matchPath(matchedNoRepeatPath, prevItem)
          )
        })
        .reverse()
      navigate(last(removedConsecutiveStack))
      setNavigationStack(removedConsecutiveStack)
    } else if (navigationStack.length > 1) {
      // Navigate to the previous location
      navigate(-1)
    } else {
      // If there's only one entry, navigate to the home page or another default route
      navigate("/")
    }
  }, [navigationStack, location.pathname, navigate])

  return {
    goBack,
  }
}
